import React, { useState, useCallback, useEffect } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import './TestDashboard.css';
import { DEFAULT_SERVERS, getDefaultApiUrl } from './apiConfig';

const STORAGE_KEY = 'test_dashboard_cases';
const SERVER_URL_KEY = 'test_dashboard_server_url';
const BATCH_COUNTER_KEY = 'test_dashboard_batch_counter';

const VEDA_OPTIONS = [
  { value: 'तैत्तिरीय', label: 'तैत्तिरीय (Taittiriya)', veda: 'Yajur' },
  { value: 'माध्यन्दिन', label: 'माध्यन्दिन (Madhyandina)', veda: 'Yajur' },
  { value: 'शाकल', label: 'शाकल (Shakala)', veda: 'Rig' },
  { value: 'शौनक', label: 'शौनक (Shaunaka)', veda: 'Atharva' },
  { value: 'रानायनीय', label: 'रानायनीय (Ranayaniya)', veda: 'Sama' },
  { value: 'कौथुम', label: 'कौथुम (Kauthuma)', veda: 'Sama' }
];

const TestDashboard = () => {
  // Server URL state
  const [serverUrl, setServerUrl] = useState(() => {
    const saved = localStorage.getItem(SERVER_URL_KEY);
    if (saved) return saved;
    return getDefaultApiUrl();
  });
  const [customServerUrl, setCustomServerUrl] = useState('');

  // Test cases state - each case is a row
  // Migration: ensure all fields exist for older saved data
  const [testCases, setTestCases] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.map(tc => ({
          ...tc,
          kramaCorrection: tc.kramaCorrection || '',
          jataCorrection: tc.jataCorrection || '',
          kramaFailingRules: tc.kramaFailingRules || [],
          jataFailingRules: tc.jataFailingRules || [],
        }));
      } catch (e) {
        console.error('Failed to parse saved test cases:', e);
      }
    }
    return [createEmptyTestCase(1)];
  });

  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'failed', or 'report'
  const [lastSubmittedId, setLastSubmittedId] = useState(null);
  const [ruleCatalog, setRuleCatalog] = useState([]);
  const [toast, setToast] = useState(null); // { message, type, links }
  const toastTimerRef = React.useRef(null);

  const showToast = (message, type = 'success', duration = 4000, links = null) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, type, links });
    if (duration > 0) {
      toastTimerRef.current = setTimeout(() => setToast(null), duration);
    }
  };

  function createEmptyTestCase(slNo) {
    return {
      id: Date.now() + Math.random(),
      slNo,
      input: '',
      userName: '',
      vedaShakha: 'तैत्तिरीय',
      kramaOutput: '',
      jataOutput: '',
      kramaOk: null,  // 'yes', 'no', or null
      jataOk: null,   // 'yes', 'no', or null
      kramaCorrection: '',  // Expected correct Krama output (when kramaOk='no')
      jataCorrection: '',   // Expected correct Jata output (when jataOk='no')
      kramaFailingRules: [],
      jataFailingRules: [],
      scholarComment: '',
      submitted: false
    };
  }

  // Fetch rule catalog when server URL changes
  useEffect(() => {
    const fetchRules = async () => {
      try {
        const response = await fetch(`${serverUrl}/v2/rules`);
        if (response.ok) {
          const data = await response.json();
          setRuleCatalog(data.categories || []);
        }
      } catch (err) {
        console.warn('Could not fetch rule catalog:', err.message);
      }
    };
    fetchRules();
  }, [serverUrl]);

  // Save to localStorage whenever test cases change
  const saveTestCases = useCallback((newCases) => {
    setTestCases(newCases);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newCases));
  }, []);

  // Save server URL to localStorage
  const handleServerChange = (url) => {
    setServerUrl(url);
    localStorage.setItem(SERVER_URL_KEY, url);
  };

  // Add a new test case
  const addTestCase = () => {
    const newSlNo = testCases.length > 0 ? Math.max(...testCases.map(tc => tc.slNo)) + 1 : 1;
    saveTestCases([...testCases, createEmptyTestCase(newSlNo)]);
  };

  // Update test case field
  const updateTestCase = (id, field, value) => {
    saveTestCases(testCases.map(tc =>
      tc.id === id ? { ...tc, [field]: value } : tc
    ));
  };

  // Call the vikrutipaatham API for a specific format
  // API: POST /v2/vikrutipaatham { "input": "text", "type": "krama"|"jata", "vedshaakha": "..." }
  const callVikrutiApi = async (inputText, type, vedaShakha) => {
    const requestBody = {
      input: inputText,
      type: type
    };
    if (vedaShakha) {
      requestBody.vedshaakha = vedaShakha;
    }
    const response = await fetch(`${serverUrl}/v2/vikrutipaatham`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API error (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    if (result.data) {
      return Array.isArray(result.data) ? result.data.join('\n') : String(result.data);
    }
    return '';
  };

  // Process a single test case
  const processTestCase = async (testCase) => {
    if (!testCase.input.trim()) return testCase;

    setProcessingId(testCase.id);

    try {
      const [kramaResult, jataResult] = await Promise.all([
        callVikrutiApi(testCase.input, 'krama', testCase.vedaShakha).catch(e => `Error: ${e.message}`),
        callVikrutiApi(testCase.input, 'jata', testCase.vedaShakha).catch(e => `Error: ${e.message}`)
      ]);

      return {
        ...testCase,
        kramaOutput: kramaResult,
        jataOutput: jataResult,
        kramaOk: null,
        jataOk: null
      };
    } finally {
      setProcessingId(null);
    }
  };

  // Process all test cases
  const processAllTestCases = async () => {
    setLoading(true);
    try {
      const processedCases = [];
      for (const testCase of testCases) {
        const processed = await processTestCase(testCase);
        processedCases.push(processed);
      }
      saveTestCases(processedCases);
    } finally {
      setLoading(false);
    }
  };

  // Process single row
  const processSingleRow = async (testCase) => {
    const processed = await processTestCase(testCase);
    saveTestCases(testCases.map(tc => tc.id === testCase.id ? processed : tc));
  };

  // Export test cases to JSON
  const exportTestCases = () => {
    const exportData = {
      version: 4,
      exportDate: new Date().toISOString(),
      serverUrl,
      testCases: testCases.map(tc => ({
        slNo: tc.slNo,
        input: tc.input,
        userName: tc.userName,
        vedaShakha: tc.vedaShakha,
        kramaOutput: tc.kramaOutput,
        jataOutput: tc.jataOutput,
        kramaOk: tc.kramaOk,
        jataOk: tc.jataOk,
        kramaCorrection: tc.kramaCorrection,
        jataCorrection: tc.jataCorrection,
        kramaFailingRules: tc.kramaFailingRules || [],
        jataFailingRules: tc.jataFailingRules || [],
        scholarComment: tc.scholarComment,
        submitted: tc.submitted
      }))
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vikruti-test-cases-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Import test cases from JSON
  const importTestCases = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (data.testCases && Array.isArray(data.testCases)) {
          const importedCases = data.testCases.map((tc, index) => ({
            id: Date.now() + index,
            slNo: tc.slNo || index + 1,
            input: tc.input || '',
            userName: tc.userName || '',
            vedaShakha: tc.vedaShakha || 'तैत्तिरीय',
            kramaOutput: tc.kramaOutput || '',
            jataOutput: tc.jataOutput || '',
            kramaOk: tc.kramaOk || null,
            jataOk: tc.jataOk || null,
            kramaCorrection: tc.kramaCorrection || '',
            jataCorrection: tc.jataCorrection || '',
            kramaFailingRules: tc.kramaFailingRules || [],
            jataFailingRules: tc.jataFailingRules || [],
            scholarComment: tc.scholarComment || '',
            submitted: tc.submitted || false
          }));
          saveTestCases(importedCases);
        }
      } catch (err) {
        console.error('Failed to import test cases:', err);
        showToast('Failed to import test cases. Invalid JSON format.', 'error');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  // Clear all test cases
  const clearAll = () => {
    if (window.confirm('Clear all test cases?')) {
      saveTestCases([createEmptyTestCase(1)]);
    }
  };

  // Handle scholar note toggle
  const handleScholarNote = (tc) => {
    if (showNoteModal === tc.id) {
      setShowNoteModal(null);
    } else {
      setShowNoteModal(tc.id);
    }
  };

  // Submit a test case (mark as reviewed)
  const submitTestCase = (tc) => {
    // Check if validation is complete
    if (!tc.kramaOutput && !tc.jataOutput) {
      showToast('Please process the test case first before submitting.', 'warn');
      return;
    }
    if (tc.kramaOk === null && tc.jataOk === null) {
      showToast('Please mark at least one output as Yes or No before submitting.', 'warn');
      return;
    }

    // Mark as submitted
    saveTestCases(testCases.map(t =>
      t.id === tc.id ? { ...t, submitted: true } : t
    ));
    setLastSubmittedId(tc.id);
  };

  // Add new row after submission
  const addRowAfterSubmit = () => {
    addTestCase();
    setLastSubmittedId(null);
  };

  // Dismiss submit confirmation
  const dismissSubmitConfirm = () => {
    setLastSubmittedId(null);
  };

  // Filter test cases based on active tab
  const filteredTestCases = activeTab === 'failed'
    ? testCases.filter(tc => tc.kramaOk === 'no' || tc.jataOk === 'no')
    : activeTab === 'report'
    ? testCases.filter(tc => tc.submitted)
    : testCases;

  // Get submitted test cases for report
  const submittedTestCases = testCases.filter(tc => tc.submitted);

  // Get failed test cases for export/send
  const failedTestCases = testCases.filter(tc => tc.kramaOk === 'no' || tc.jataOk === 'no');

  // Map vedaShakha to veda name
  const getVedaFromShakha = (shakha) => {
    const vedaMap = {
      'तैत्तिरीय': 'Yajurveda',
      'माध्यन्दिन': 'Yajurveda',
      'शाकल': 'Rigveda',
      'शौनक': 'Atharvaveda',
      'रानायनीय': 'Samaveda',
      'कौथुम': 'Samaveda'
    };
    return vedaMap[shakha] || 'Yajurveda';
  };

  // Get next batch number (auto-incrementing from localStorage)
  const getNextBatchNumber = () => {
    const current = parseInt(localStorage.getItem(BATCH_COUNTER_KEY) || '0', 10);
    const next = current + 1;
    localStorage.setItem(BATCH_COUNTER_KEY, String(next));
    return next;
  };

  // Build batch payload from failed test cases
  const buildBatchPayload = () => {
    const batchNumber = getNextBatchNumber();
    const timestamp = new Date().toISOString();

    const reports = [];
    failedTestCases.forEach(tc => {
      if (tc.kramaOk === 'no') {
        reports.push({
          sl_no: tc.slNo,
          input: tc.input,
          veda: getVedaFromShakha(tc.vedaShakha),
          veda_shakha: tc.vedaShakha,
          user_name: tc.userName || 'Anonymous',
          format_type: 'krama',
          actual_output: tc.kramaOutput,
          expected_output: tc.kramaCorrection || '',
          error_type: 'mismatch',
          notes: tc.scholarComment || `Test case #${tc.slNo} - Krama output marked incorrect`,
          failing_rules: tc.kramaFailingRules || []
        });
      }
      if (tc.jataOk === 'no') {
        reports.push({
          sl_no: tc.slNo,
          input: tc.input,
          veda: getVedaFromShakha(tc.vedaShakha),
          veda_shakha: tc.vedaShakha,
          user_name: tc.userName || 'Anonymous',
          format_type: 'jata',
          actual_output: tc.jataOutput,
          expected_output: tc.jataCorrection || '',
          error_type: 'mismatch',
          notes: tc.scholarComment || `Test case #${tc.slNo} - Jata output marked incorrect`,
          failing_rules: tc.jataFailingRules || []
        });
      }
    });

    return {
      batch_number: batchNumber,
      timestamp,
      total_reports: reports.length,
      reports
    };
  };

  // Save batch JSON locally as a file download (backup)
  const saveBatchLocally = (batchPayload) => {
    const blob = new Blob([JSON.stringify(batchPayload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `test_report_batch_${batchPayload.batch_number}_${batchPayload.timestamp.split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Send failed tests to server as a batch
  // API: POST /v2/testcases/failed/batch
  const sendFailedToServer = async () => {
    if (failedTestCases.length === 0) {
      showToast('No failed test cases to send.', 'warn');
      return;
    }

    const batchPayload = buildBatchPayload();

    try {
      const response = await fetch(`${serverUrl}/v2/testcases/failed/batch`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(batchPayload)
      });

      if (response.ok) {
        const result = await response.json();
        const issueLinks = (result.results || [])
          .filter(r => r.github_issue_number && r.github_issue_url)
          .map(r => ({ label: `#${r.github_issue_number}`, url: r.github_issue_url }));
        const msg = `${result.total_processed} report(s) submitted.`;
        // duration=0 means click-to-dismiss (no auto-hide)
        showToast(msg, 'success', 0, issueLinks.length > 0 ? issueLinks : null);
        saveBatchLocally(batchPayload);
      } else {
        const errorBody = await response.text().catch(() => '');
        console.error(`Failed to submit batch #${batchPayload.batch_number}: HTTP ${response.status}: ${errorBody}. Server: ${serverUrl}`);
        showToast(`Failed to create issue. Server error (${response.status}). Check server URL setting.`, 'error', 8000);
      }
    } catch (err) {
      console.error(`Network error submitting batch #${batchPayload.batch_number}: ${err.message}. Server: ${serverUrl}`);
      showToast(`Failed to create issue. Could not reach server (${serverUrl}). Check server URL setting.`, 'error', 8000);
    }
  };

  // Rule Picker component for selecting failing rules
  const RulePicker = ({ selectedRules, onChange, formatType }) => {
    const [expandedGroups, setExpandedGroups] = useState({ vikruti: true });
    const formatKey = formatType.toLowerCase(); // 'krama' or 'jata'

    const toggleGroup = (groupId) => {
      setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
    };

    const toggleRule = (ruleId) => {
      if (selectedRules.includes(ruleId)) {
        onChange(selectedRules.filter(r => r !== ruleId));
      } else {
        onChange([...selectedRules, ruleId]);
      }
    };

    // Filter catalog:
    // - Remove svara (accent) categories
    // - For vikruti category: keep only the rule matching this format type
    const filteredCatalog = ruleCatalog
      .filter(category => {
        const id = (category.id || '').toLowerCase();
        const name = (category.name || '').toLowerCase();
        return !id.includes('accent') && !name.includes('accent');
      })
      .map(category => {
        if (category.section === 'vikruti') {
          // Only keep the rule for this specific format type
          const matchingRules = category.rules.filter(r =>
            r.rule_id === `vikruti-${formatKey}`
          );
          return { ...category, rules: matchingRules };
        }
        return category;
      })
      .filter(category => category.rules.length > 0);

    if (!filteredCatalog.length) return null;

    // Group categories by section
    const sections = [];
    const seen = new Set();
    filteredCatalog.forEach(category => {
      const sectionId = category.section || category.id;
      if (!seen.has(sectionId)) {
        seen.add(sectionId);
        sections.push({
          id: sectionId,
          name: category.section_name || category.name,
          categories: filteredCatalog.filter(c => (c.section || c.id) === sectionId)
        });
      }
    });

    // Check if a rule is the vikruti format rule (always triggered for this format)
    const isTriggeredRule = (ruleId) => ruleId === `vikruti-${formatKey}`;

    return (
      <div className="rule-picker">
        <div className="rule-picker-header">
          <span className="rule-picker-label">Suspected Failing Rules ({formatType}):</span>
          <span className="rule-picker-header-right">
            {selectedRules.length > 0 && (
              <span className="selected-rules-badge">{selectedRules.length} selected</span>
            )}
            <a href="/rules-reference.html" target="_blank" rel="noreferrer" className="rules-ref-link" title="Rules Reference">
              ?
            </a>
          </span>
        </div>
        <div className="rule-picker-groups">
          {sections.map(section => (
            <div key={section.id} className="rule-section">
              <div className="rule-section-header">{section.name}</div>
              {section.categories.map(category => (
                <div key={category.id} className="rule-group">
                  <button
                    type="button"
                    className={`rule-group-header ${expandedGroups[category.id] ? 'expanded' : ''}`}
                    onClick={() => toggleGroup(category.id)}
                  >
                    <span className="rule-group-toggle">{expandedGroups[category.id] ? '\u25BE' : '\u25B8'}</span>
                    <span className="rule-group-name">{category.name}</span>
                    <span className="rule-group-count">{category.rules.length}</span>
                  </button>
                  {expandedGroups[category.id] && (
                    <div className="rule-group-items">
                      {category.rules.map(rule => (
                        <label
                          key={rule.rule_id}
                          className={`rule-checkbox-label ${isTriggeredRule(rule.rule_id) ? 'rule-triggered' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedRules.includes(rule.rule_id)}
                            onChange={() => toggleRule(rule.rule_id)}
                          />
                          <span className="rule-checkbox-name">{rule.rule_name}</span>
                          {isTriggeredRule(rule.rule_id) && (
                            <span className="rule-triggered-badge">triggered</span>
                          )}
                          {rule.sutra_ref && (
                            <span className="rule-checkbox-ref">({rule.sutra_ref})</span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Container fluid className="test-dashboard">
      {/* Inline Toast Notification */}
      {toast && (
        <div className={`toast-bar toast-${toast.type} toast-fixed`}>
          <span className="toast-message">
            {toast.message}
            {toast.links && toast.links.map((link, i) => (
              <a key={i} href={link.url} target="_blank" rel="noreferrer" className="toast-link">
                {link.label}
              </a>
            ))}
          </span>
          <button className="toast-close" onClick={() => setToast(null)}>&times;</button>
        </div>
      )}

      {/* Header */}
      <Row className="dashboard-header">
        <Col>
          <h1 className="dashboard-title">परीक्षा-फलकम् (Test Dashboard)</h1>
          <p className="dashboard-subtitle">Vikruti API Verification - Krama & Jata Outputs</p>
        </Col>
      </Row>

      {/* Server Selection */}
      <Row className="server-row">
        <Col>
          <div className="server-selector">
            <label className="server-label">Server URL:</label>
            <select
              value={serverUrl === DEFAULT_SERVERS.localhost || serverUrl === DEFAULT_SERVERS.production ? serverUrl : 'custom'}
              onChange={(e) => {
                if (e.target.value === 'custom') {
                  setCustomServerUrl(serverUrl);
                } else {
                  handleServerChange(e.target.value);
                }
              }}
              className="server-select"
            >
              <option value={DEFAULT_SERVERS.localhost}>Localhost (8000)</option>
              <option value={DEFAULT_SERVERS.production}>Production</option>
              <option value="custom">Custom...</option>
            </select>
            {serverUrl !== DEFAULT_SERVERS.localhost && serverUrl !== DEFAULT_SERVERS.production && (
              <input
                type="text"
                value={customServerUrl || serverUrl}
                onChange={(e) => setCustomServerUrl(e.target.value)}
                onBlur={() => customServerUrl && handleServerChange(customServerUrl)}
                placeholder="Enter custom server URL"
                className="server-custom-input"
              />
            )}
            <span className={`server-status ${serverUrl.includes('localhost') ? 'local' : 'remote'}`}>
              {serverUrl.includes('localhost') ? 'Local' : 'Remote'}
            </span>
          </div>
        </Col>
      </Row>

      {/* Action Buttons */}
      <Row className="actions-row">
        <Col>
          <div className="action-buttons">
            <Button
              variant="primary"
              onClick={processAllTestCases}
              disabled={loading}
              className="process-btn"
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Processing...
                </>
              ) : (
                'Process All'
              )}
            </Button>
            <Button variant="outline-secondary" onClick={addTestCase} className="add-btn">
              + Add Row
            </Button>
            <Button variant="outline-info" onClick={exportTestCases} className="export-btn">
              Export JSON
            </Button>
            <label className="import-btn btn btn-outline-info">
              Import JSON
              <input type="file" accept=".json" onChange={importTestCases} hidden />
            </label>
            <Button variant="outline-danger" onClick={clearAll} className="clear-btn">
              Clear All
            </Button>
          </div>
        </Col>
      </Row>

      {/* Tab Navigation */}
      <Row className="tab-row">
        <Col>
          <div className="tab-buttons">
            <button
              className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All Tests
              <span className="tab-badge all-badge">{testCases.length}</span>
            </button>
            <button
              className={`tab-btn failed-tab ${activeTab === 'failed' ? 'active' : ''}`}
              onClick={() => setActiveTab('failed')}
            >
              Failed Tests
              <span className="tab-badge failed-badge">
                {testCases.filter(tc => tc.kramaOk === 'no' || tc.jataOk === 'no').length}
              </span>
            </button>
            <button
              className={`tab-btn ${activeTab === 'report' ? 'active' : ''}`}
              onClick={() => setActiveTab('report')}
            >
              Report
              <span className="tab-badge all-badge">
                {submittedTestCases.length}
              </span>
            </button>
          </div>
        </Col>
      </Row>

      {/* Test Cases Table */}
      <Row className="table-row">
        <Col>
          <div className="test-table-container">
            <table className="test-table test-table-rows">
              <thead>
                <tr>
                  <th className="col-sl">Sl</th>
                  <th className="col-user">User</th>
                  <th className="col-input">Input (Samhita)</th>
                  <th style={{ width: '90px', minWidth: '90px' }}>Shakha</th>
                  <th className="col-process">Process</th>
                  <th className="col-output">Krama Output</th>
                  <th className="col-validation">Krama OK?</th>
                  <th className="col-output">Jata Output</th>
                  <th className="col-validation">Jata OK?</th>
                  <th className="col-note">Note</th>
                  <th className="col-submit">Submit</th>
                </tr>
              </thead>
              <tbody>
                {filteredTestCases.map((tc) => (
                  <React.Fragment key={tc.id}>
                    <tr className={`${processingId === tc.id ? 'processing-row' : ''} ${tc.kramaOk === 'yes' && tc.jataOk === 'yes' ? 'all-ok-row' : ''}`}>
                      {/* Serial Number */}
                      <td className="cell-sl">{tc.slNo}</td>

                      {/* User Name */}
                      <td className="cell-user">
                        <input
                          type="text"
                          value={tc.userName}
                          onChange={(e) => updateTestCase(tc.id, 'userName', e.target.value)}
                          placeholder="Name"
                          className="user-input"
                        />
                      </td>

                      {/* Input */}
                      <td className="cell-input">
                        <textarea
                          value={tc.input}
                          onChange={(e) => updateTestCase(tc.id, 'input', e.target.value)}
                          placeholder="संहिता-पाठं लिखतु..."
                          className="test-input"
                          disabled={processingId === tc.id}
                        />
                      </td>

                      {/* Veda Shakha */}
                      <td style={{ background: '#f5f5f5', verticalAlign: 'top', padding: '6px' }}>
                        <select
                          value={tc.vedaShakha}
                          onChange={(e) => updateTestCase(tc.id, 'vedaShakha', e.target.value)}
                          style={{ width: '100%', padding: '4px', fontSize: '0.8rem', borderRadius: '4px', border: '1px solid #cfd8dc' }}
                        >
                          {VEDA_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.value}</option>
                          ))}
                        </select>
                      </td>

                      {/* Process Button */}
                      <td className="cell-process">
                        <button
                          className="row-process-btn"
                          onClick={() => processSingleRow(tc)}
                          disabled={loading || processingId === tc.id || !tc.input.trim()}
                          title="Process this row"
                        >
                          {processingId === tc.id ? '...' : '▶'}
                        </button>
                      </td>

                      {/* Krama Output */}
                      <td className={`cell-output ${tc.kramaOk === 'yes' ? 'output-yes' : tc.kramaOk === 'no' ? 'output-no' : ''}`}>
                        <textarea
                          value={tc.kramaOutput}
                          readOnly
                          placeholder={processingId === tc.id ? 'Loading...' : 'Krama result...'}
                          className="test-output"
                        />
                        {tc.kramaOk === 'no' && (
                          <div className="correction-block">
                            <label className="correction-label">सम्यक् रूपम् (Expected):</label>
                            <textarea
                              value={tc.kramaCorrection || ''}
                              onChange={(e) => updateTestCase(tc.id, 'kramaCorrection', e.target.value)}
                              placeholder="सम्यक् क्रमपाठं लिखतु..."
                              className="test-correction"
                            />
                            <RulePicker
                              selectedRules={tc.kramaFailingRules || []}
                              onChange={(rules) => updateTestCase(tc.id, 'kramaFailingRules', rules)}
                              formatType="Krama"
                            />
                          </div>
                        )}
                      </td>

                      {/* Krama OK? Yes/No */}
                      <td className="cell-validation">
                        <div className="yes-no-buttons">
                          <button
                            className={`yn-btn yes-btn ${tc.kramaOk === 'yes' ? 'active' : ''}`}
                            onClick={() => updateTestCase(tc.id, 'kramaOk', 'yes')}
                            disabled={!tc.kramaOutput || tc.kramaOutput.startsWith('Error')}
                          >
                            Yes
                          </button>
                          <button
                            className={`yn-btn no-btn ${tc.kramaOk === 'no' ? 'active' : ''}`}
                            onClick={() => updateTestCase(tc.id, 'kramaOk', 'no')}
                            disabled={!tc.kramaOutput || tc.kramaOutput.startsWith('Error')}
                          >
                            No
                          </button>
                        </div>
                      </td>

                      {/* Jata Output */}
                      <td className={`cell-output ${tc.jataOk === 'yes' ? 'output-yes' : tc.jataOk === 'no' ? 'output-no' : ''}`}>
                        <textarea
                          value={tc.jataOutput}
                          readOnly
                          placeholder={processingId === tc.id ? 'Loading...' : 'Jata result...'}
                          className="test-output"
                        />
                        {tc.jataOk === 'no' && (
                          <div className="correction-block">
                            <label className="correction-label">सम्यक् रूपम् (Expected):</label>
                            <textarea
                              value={tc.jataCorrection || ''}
                              onChange={(e) => updateTestCase(tc.id, 'jataCorrection', e.target.value)}
                              placeholder="सम्यक् जटापाठं लिखतु..."
                              className="test-correction"
                            />
                            <RulePicker
                              selectedRules={tc.jataFailingRules || []}
                              onChange={(rules) => updateTestCase(tc.id, 'jataFailingRules', rules)}
                              formatType="Jata"
                            />
                          </div>
                        )}
                      </td>

                      {/* Jata OK? Yes/No */}
                      <td className="cell-validation">
                        <div className="yes-no-buttons">
                          <button
                            className={`yn-btn yes-btn ${tc.jataOk === 'yes' ? 'active' : ''}`}
                            onClick={() => updateTestCase(tc.id, 'jataOk', 'yes')}
                            disabled={!tc.jataOutput || tc.jataOutput.startsWith('Error')}
                          >
                            Yes
                          </button>
                          <button
                            className={`yn-btn no-btn ${tc.jataOk === 'no' ? 'active' : ''}`}
                            onClick={() => updateTestCase(tc.id, 'jataOk', 'no')}
                            disabled={!tc.jataOutput || tc.jataOutput.startsWith('Error')}
                          >
                            No
                          </button>
                        </div>
                      </td>

                      {/* Scholar Note */}
                      <td className="cell-note">
                        <button
                          className={`note-toggle-btn ${tc.scholarComment ? 'has-note' : ''}`}
                          onClick={() => handleScholarNote(tc)}
                          title={tc.scholarComment || 'Add note'}
                        >
                          {showNoteModal === tc.id ? '✓' : tc.scholarComment ? '📝' : '+'}
                        </button>
                      </td>

                      {/* Submit */}
                      <td className="cell-submit">
                        {tc.submitted ? (
                          <span className="submitted-badge" title="Submitted">✓</span>
                        ) : (
                          <button
                            className="submit-btn"
                            onClick={() => submitTestCase(tc)}
                            disabled={!tc.kramaOutput && !tc.jataOutput}
                            title="Submit this test case"
                          >
                            Submit
                          </button>
                        )}
                      </td>
                    </tr>

                    {/* Submit Confirmation Row */}
                    {lastSubmittedId === tc.id && (
                      <tr className="submit-confirm-row">
                        <td colSpan="11" className="submit-confirm-cell">
                          <div className="submit-confirm-container">
                            <span className="submit-confirm-text">
                              Thanks for your input! Want to submit one more?
                            </span>
                            <button className="submit-confirm-yes" onClick={addRowAfterSubmit}>
                              Yes, Add Row
                            </button>
                            <button className="submit-confirm-no" onClick={dismissSubmitConfirm}>
                              No, Done
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}

                    {/* Scholar Note Row */}
                    {showNoteModal === tc.id && (
                      <tr className="note-row">
                        <td colSpan="11" className="note-cell">
                          <div className="note-container">
                            <label>Scholar Note:</label>
                            <textarea
                              value={tc.scholarComment}
                              onChange={(e) => updateTestCase(tc.id, 'scholarComment', e.target.value)}
                              placeholder="Enter scholar comment or note..."
                              className="scholar-note-input"
                              autoFocus
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Col>
      </Row>

      {/* Summary */}
      <Row className="info-row">
        <Col>
          {activeTab === 'failed' ? (
            <div className="info-panel failed-panel">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <span className="info-text">
                  Failed Tests: {failedTestCases.length} |
                  Krama Failed: {failedTestCases.filter(tc => tc.kramaOk === 'no').length} |
                  Jata Failed: {failedTestCases.filter(tc => tc.jataOk === 'no').length}
                </span>
                <Button
                  variant="danger"
                  onClick={sendFailedToServer}
                  disabled={failedTestCases.length === 0}
                  size="sm"
                >
                  Create Issue
                </Button>
              </div>
            </div>
          ) : activeTab === 'report' ? (
            <div className="info-panel report-panel">
              <div className="report-summary">
                <h5 className="report-title">Test Report Summary</h5>
                <div className="report-stats">
                  <div className="report-stat">
                    <span className="stat-label">Total Submitted:</span>
                    <span className="stat-value">{submittedTestCases.length}</span>
                  </div>
                  <div className="report-stat passed">
                    <span className="stat-label">Krama Passed:</span>
                    <span className="stat-value">{submittedTestCases.filter(tc => tc.kramaOk === 'yes').length}</span>
                  </div>
                  <div className="report-stat failed">
                    <span className="stat-label">Krama Failed:</span>
                    <span className="stat-value">{submittedTestCases.filter(tc => tc.kramaOk === 'no').length}</span>
                  </div>
                  <div className="report-stat passed">
                    <span className="stat-label">Jata Passed:</span>
                    <span className="stat-value">{submittedTestCases.filter(tc => tc.jataOk === 'yes').length}</span>
                  </div>
                  <div className="report-stat failed">
                    <span className="stat-label">Jata Failed:</span>
                    <span className="stat-value">{submittedTestCases.filter(tc => tc.jataOk === 'no').length}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="info-panel">
              <span className="info-text">
                Total: {testCases.length} |
                Krama OK: {testCases.filter(tc => tc.kramaOk === 'yes').length} Yes, {testCases.filter(tc => tc.kramaOk === 'no').length} No |
                Jata OK: {testCases.filter(tc => tc.jataOk === 'yes').length} Yes, {testCases.filter(tc => tc.jataOk === 'no').length} No |
                Submitted: {submittedTestCases.length}
              </span>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default TestDashboard;
